<template>
  <div class="publishers-products-section">
    <!-- Ghost / Skeleton loading -->
    <div v-if="loading" class="publishers-skeleton" aria-hidden="true">
      <section
        v-for="n in skeletonSections"
        :key="'skeleton-section-' + n"
        class="product-section skeleton-section"
      >
        <div class="custom-container">
          <div class="carousel-header mt-4 mb-4">
            <div class="skeleton-title skeleton-pulse"></div>
            <div class="skeleton-link skeleton-pulse"></div>
          </div>
          <div class="skeleton-carousel">
            <div
              v-for="c in skeletonCards"
              :key="'skeleton-card-' + n + '-' + c"
              class="skeleton-card skeleton-pulse"
            >
              <div class="skeleton-image"></div>
              <div class="skeleton-line short"></div>
              <div class="skeleton-line"></div>
              <div class="skeleton-line price"></div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Loaded publishers -->
    <transition name="publishers-fade">
      <div v-if="!loading && visiblePublishers.length" class="publishers-list">
        <PublisherProductsCarousel
          v-for="publisher in visiblePublishers"
          :key="publisher.id"
          :title="publisher.name"
          :brand-id="publisher.id"
          :brand-name="publisher.name"
          :products="publisher.products"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import PublisherProductsCarousel from "@/components/slider/PublisherProductsCarousel.component";

export default {
  name: "PublishersProducts",
  components: {
    PublisherProductsCarousel
  },
  data() {
    return {
      loading: true,
      publishers: [],
      skeletonSections: 3,
      skeletonCards: 6
    };
  },
  computed: {
    visiblePublishers() {
      return (this.publishers || []).filter(
        publisher =>
          publisher &&
          Array.isArray(publisher.products) &&
          publisher.products.length > 0
      );
    }
  },
  created() {
    this.fetchPublishersProducts();
  },
  methods: {
    fetchPublishersProducts() {
      this.loading = true;
      this.$store
        .dispatch("getMainLevelPublishersProducts")
        .then(result => {
          const items = (result && result.items) || [];
          // Keep API order (main_order); skip empty product lists in computed
          this.publishers = items.slice().sort((a, b) => {
            const orderA = Number(a.main_order) || 0;
            const orderB = Number(b.main_order) || 0;
            return orderA - orderB;
          });
          this.loading = false;
        })
        .catch(() => {
          // Fail silently so homepage stays intact
          this.publishers = [];
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.publishers-products-section {
  width: 100%;
}

.product-section {
  padding: 2rem 0;
}

.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.skeleton-title {
  width: 160px;
  height: 28px;
  border-radius: 4px;
}

.skeleton-link {
  width: 90px;
  height: 18px;
  border-radius: 4px;
}

.skeleton-carousel {
  display: flex;
  gap: 1rem;
  overflow: hidden;
  min-height: 350px;
}

.skeleton-card {
  flex: 0 0 calc((100% - 5rem) / 6);
  max-width: calc((100% - 5rem) / 6);
  height: 350px;
  border: 1px solid #e7e7e7;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.75rem;
  box-sizing: border-box;
}

.skeleton-image {
  width: 140px;
  height: 220px;
  background: #ececec;
  border-radius: 2px;
  margin-bottom: 1rem;
}

.skeleton-line {
  width: 80%;
  height: 12px;
  background: #ececec;
  border-radius: 2px;
  margin: 6px 0;
}

.skeleton-line.short {
  width: 55%;
}

.skeleton-line.price {
  width: 40%;
  margin-top: 12px;
}

.skeleton-pulse {
  position: relative;
  overflow: hidden;
  background: #f3f3f3;
}

.skeleton-pulse::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.45),
    transparent
  );
  animation: skeleton-shimmer 1.4s infinite;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.publishers-fade-enter-active {
  transition: opacity 0.35s ease;
}

.publishers-fade-enter {
  opacity: 0;
}

@media screen and (max-width: 1366px) {
  .skeleton-card {
    flex-basis: calc((100% - 4rem) / 5);
    max-width: calc((100% - 4rem) / 5);
  }
}

@media screen and (max-width: 1024px) {
  .skeleton-card {
    flex-basis: calc((100% - 3rem) / 4);
    max-width: calc((100% - 3rem) / 4);
  }
}

@media screen and (max-width: 768px) {
  .skeleton-card {
    flex-basis: calc((100% - 2rem) / 3);
    max-width: calc((100% - 2rem) / 3);
  }

  .skeleton-cards-hide-tablet {
    display: none;
  }
}

@media screen and (max-width: 600px) {
  .skeleton-card {
    flex-basis: calc((100% - 1rem) / 2);
    max-width: calc((100% - 1rem) / 2);
  }

  .skeleton-carousel .skeleton-card:nth-child(n + 3) {
    display: none;
  }
}
</style>
